import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/api/client';
import { PERMISSIONS, type SourceResponse } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { Callout } from '@/components/Callout';
import { Select } from '@/components/Select';
import { StatusChip } from '@/components/ops/Badges';
import { CheckboxField, TextField } from '@/components/ops/Forms';
import { PageHeader, Section } from '@/components/ops/Layout';
import { Modal } from '@/components/ops/Modal';
import { MutationError } from '@/components/ops/Problem';

export const PROVIDERS = [
  { value: 'ga4', label: 'Google Analytics 4', hint: 'visits, sources, pages' },
  { value: 'clarity', label: 'Microsoft Clarity', hint: 'behaviour, frustration, recordings' },
  { value: 'tavi_leads', label: 'Tavi website leads', hint: 'leads and unique visitors' },
];
const providerLabel = (key: string) => PROVIDERS.find((p) => p.value === key)?.label ?? key;

const TIME_ZONES = ['Africa/Cairo', 'Asia/Riyadh', 'Asia/Dubai', 'Asia/Kuwait', 'Europe/London', 'UTC'];

/** A site key is how the website pages group sources: lowercase letters, digits and dashes. */
export function siteKeyProblem(key: string): string | null {
  const v = key.trim();
  if (!v) return 'Required.';
  return /^[a-z0-9][a-z0-9-]{0,63}$/.test(v) ? null : 'Lowercase letters, digits and dashes only.';
}

/** The provider's own settings: GA4 needs its property id; the others need nothing here. */
export function sourceConfig(provider: string, id: string): Record<string, string> | null {
  const v = id.trim();
  if (provider === 'ga4') return v ? { property_id: v } : null;
  if (provider === 'clarity') return v ? { project_id: v } : {};
  return {};
}

/**
 * Website sources: where the traffic, behaviour and lead figures come from. Add or edit one here (owner); the provider
 * credentials — the GA4 service account and the Clarity token — are server settings, never typed into the console.
 */
export function SourcesPage() {
  const queryClient = useQueryClient();
  const canIngest = hasPermission(PERMISSIONS.ingestionRun);
  const canManage = hasPermission(PERMISSIONS.sourceManage);
  const [editing, setEditing] = useState<SourceResponse | 'new' | null>(null);

  const { data, isLoading, isError, error } = useQuery({ queryKey: ['sources'], queryFn: api.listSources });

  const ingest = useMutation({
    mutationFn: (sourceId: string) => api.ingest(sourceId),
    // Both the source list (last_ingested_date) and every overview read change.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['sources'] });
      void queryClient.invalidateQueries({ queryKey: ['overview'] });
    },
  });

  if (isLoading) return <div className="empty-state"><span className="spinner" /></div>;

  if (isError) {
    return (
      <Callout tone="error" title="Could not load sources">
        {error instanceof ApiError ? error.message : 'Unexpected error.'}
      </Callout>
    );
  }

  const sources = data ?? [];
  const siteKeys = [...new Set(sources.map((s) => s.site_key))];

  return (
    <>
      <PageHeader
        title="Website sources"
        subtitle="Where the website traffic, behaviour and lead figures come from."
        actions={canManage && <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>Add a source</button>}
      />

      <Section title="Connected sources" meta={`${sources.length} source${sources.length === 1 ? '' : 's'}`} flush>
        {sources.length === 0 ? (
          <div className="empty-state t-body">No analytics sources connected yet.{canManage ? ' Add one to see the website pages.' : ''}</div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Site</th>
                  <th>Time zone</th>
                  <th>Status</th>
                  <th>Last collected</th>
                  {(canIngest || canManage) && <th />}
                </tr>
              </thead>
              <tbody>
                {sources.map((source) => (
                  <tr key={source.id}>
                    <td>
                      <span style={{ fontWeight: 600 }}>{source.display_name}</span>
                      <div className="t-caption muted">{providerLabel(source.provider)}{source.project_id ? ` · project ${source.project_id}` : ''}</div>
                    </td>
                    <td>{source.site_key}</td>
                    <td className="muted">{source.reporting_time_zone}</td>
                    <td><StatusChip status={source.enabled ? 'Enabled' : 'Disabled'} tone={source.enabled ? 'success' : 'muted'} /></td>
                    <td className="tabular">{source.last_ingested_date ?? <span className="subtle">never</span>}</td>
                    {(canIngest || canManage) && (
                      <td>
                        <span className="row" style={{ justifyContent: 'flex-end' }}>
                          {canIngest && (
                            <button className="btn btn-ghost btn-sm" disabled={ingest.isPending || !source.enabled} onClick={() => ingest.mutate(source.id)}>
                              Collect yesterday
                            </button>
                          )}
                          {canManage && <button className="btn btn-ghost btn-sm" onClick={() => setEditing(source)}>Edit</button>}
                        </span>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <MutationError error={ingest.error} />
      {ingest.data && (
        <Callout tone={ingest.data.error ? 'error' : 'info'} title={`Collection: ${ingest.data.status}`}>
          {ingest.data.error
            ? ingest.data.error
            : ingest.data.skip_reason
              ? `Skipped — ${ingest.data.skip_reason}. Clarity allows only 10 requests per project per day.`
              : `${ingest.data.rows_written} rows written.`}
        </Callout>
      )}

      <Callout tone="warning" title="Microsoft Clarity cannot backfill">
        Clarity's export API serves only the last 1–3 days and allows 10 requests per project per
        day. Any day the scheduled job does not run is lost permanently — it cannot be recovered
        later from Clarity or from here.
      </Callout>

      {editing && (
        <SourceDialog source={editing === 'new' ? null : editing} siteKeys={siteKeys}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void queryClient.invalidateQueries({ queryKey: ['sources'] }); }} />
      )}
    </>
  );
}

function SourceDialog({
  source, siteKeys, onClose, onSaved,
}: {
  source: SourceResponse | null;
  siteKeys: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [provider, setProvider] = useState(source?.provider ?? 'ga4');
  const [siteKey, setSiteKey] = useState(source?.site_key ?? siteKeys[0] ?? '');
  const [name, setName] = useState(source?.display_name ?? '');
  const [zone, setZone] = useState(source?.reporting_time_zone ?? 'Africa/Cairo');
  const [enabled, setEnabled] = useState(source?.enabled ?? true);
  // Not read back for GA4 (the list does not return the config): empty on edit keeps the stored id.
  const [providerId, setProviderId] = useState(source?.project_id ?? '');
  const [tried, setTried] = useState(false);

  const config = sourceConfig(provider, providerId);
  const siteProblem = source ? null : siteKeyProblem(siteKey);
  const idProblem = !source && provider === 'ga4' && config === null ? 'The GA4 property id is required (Admin → Property settings).' : null;

  const save = useMutation({
    mutationFn: () => source
      ? api.updateSource(source.id, {
        display_name: name.trim() || undefined,
        reporting_time_zone: zone,
        enabled,
        ...(providerId.trim() && providerId.trim() !== (source.project_id ?? '') ? { config: sourceConfig(provider, providerId) ?? undefined } : {}),
      })
      : api.createSource({ provider, site_key: siteKey.trim(), display_name: name.trim(), reporting_time_zone: zone, config: config ?? {} }),
    onSuccess: onSaved,
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTried(true);
    if (!siteProblem && !idProblem) save.mutate();
  };

  return (
    <Modal title={source ? `Edit ${source.display_name}` : 'Add a website source'} onClose={save.isPending ? undefined : onClose} dismissible={!save.isPending}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={save.isPending}>Cancel</button>
          <button type="submit" form="source-form" className="btn btn-primary" disabled={save.isPending}>{save.isPending ? 'Saving…' : source ? 'Save' : 'Add source'}</button>
        </>
      }>
      <form id="source-form" className="stack" onSubmit={submit} noValidate>
        {source ? (
          <p className="t-body" style={{ margin: 0 }}>{providerLabel(source.provider)} · site {source.site_key}</p>
        ) : (
          <>
            <Select label="Provider" value={provider} onChange={setProvider} searchable={false} options={PROVIDERS} />
            <TextField label="Site key" value={siteKey} onChange={setSiteKey} mono required placeholder="tavi-landing"
              error={tried ? siteProblem : null}
              hint={siteKeys.length ? `Existing sites: ${siteKeys.join(', ')} — reuse one to add a provider to the same site.` : 'Groups the sources of one website.'} />
          </>
        )}
        <TextField label="Name" value={name} onChange={setName} placeholder={`${providerLabel(provider)} — ${siteKey || 'site'}`} />
        {provider === 'ga4' && (
          <TextField label="GA4 property id" value={providerId} onChange={setProviderId} mono required={!source} placeholder="123456789"
            error={tried ? idProblem : null}
            hint={source ? 'Leave empty to keep the stored id.' : 'The service account must be a Viewer on this property.'} />
        )}
        {provider === 'clarity' && (
          <TextField label="Clarity project id (optional)" value={providerId} onChange={setProviderId} mono
            hint="Public — it is in the page snippet. Used to open recordings." />
        )}
        <Select label="Reporting time zone" value={zone} onChange={setZone} searchable={false}
          options={[...new Set([zone, ...TIME_ZONES])].map((z) => ({ value: z, label: z }))} hint="Days are cut at midnight in this zone." />
        {source && <CheckboxField label="Collect from this source" checked={enabled} onChange={setEnabled} hint="Off stops the scheduled collection; the stored days stay." />}
        <p className="t-caption muted" style={{ margin: 0 }}>
          The provider credentials (the GA4 service account, the Clarity token) are server settings — they are never typed here.
        </p>
        <MutationError error={save.error} />
      </form>
    </Modal>
  );
}
